import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { Loading, ErrorView } from '../components/ViewState';
import { api } from '../data/api';
import { useAllClients } from '../data/store';
import type { VaultEntry, VaultKind } from '../data/types';

const KIND_LABEL: Record<VaultKind, string> = {
  registrar: 'Dominio / Registrar',
  hosting: 'Hosting',
  wordpress: 'WordPress',
  smtp: 'Email / SMTP',
  api: 'API',
  altro: 'Altro',
};
const KINDS = Object.keys(KIND_LABEL) as VaultKind[];

export function CassaforteView() {
  const [entries, setEntries] = useState<VaultEntry[] | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, { secret: string; username: string | null }>>({});

  const load = async () => {
    try {
      setEntries(await api.vault());
      setNotConfigured(false);
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      if (m === 'vault_not_configured') setNotConfigured(true);
      else setErr(m);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const reveal = async (id: string) => {
    try {
      const r = await api.revealSecret(id);
      setRevealed((p) => ({ ...p, [id]: r }));
      // auto-hide after 30s so secrets don't linger on screen
      setTimeout(() => setRevealed((p) => { const n = { ...p }; delete n[id]; return n; }), 30_000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Errore: ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  const hide = (id: string) => setRevealed((p) => { const n = { ...p }; delete n[id]; return n; });
  const remove = async (id: string, label: string) => {
    // eslint-disable-next-line no-alert
    if (!confirm(`Eliminare "${label}"?`)) return;
    await api.deleteSecret(id);
    setEntries((p) => p?.filter((e) => e.id !== id) ?? null);
  };

  if (notConfigured) return <SetupNote />;
  if (err) return <ErrorView detail={err} />;
  if (!entries) return <Loading />;

  return (
    <div className="view">
      <div className="view-pad rs-stack">
        <div className="rs-actionbar split">
          <span className="rs-muted" style={{ fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="lock" size={13} /> {entries.length} credenziali · cifrate AES-256
          </span>
          <button type="button" className="btn btn-primary" onClick={() => setNewOpen(true)}>
            <Icon name="plus" size={16} />
            <span className="btn-label">Nuovo segreto</span>
          </button>
        </div>

        <div className="rs-card" style={{ padding: 0 }}>
          {entries.length ? (
            entries.map((e) => {
              const r = revealed[e.id];
              return (
                <div className="vault-row" key={e.id}>
                  <span className="rs-chip cat vault-kind">{KIND_LABEL[e.kind]}</span>
                  <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1, gap: 4 }}>
                    <span className="ops-name">{e.label}</span>
                    <span className="rs-cli-meta">{[e.username, e.tenantName].filter(Boolean).join(' · ') || '—'}</span>
                    {r && (
                      <span className="vault-secret">
                        {r.username && <code>{r.username}</code>}
                        <code className="vault-val">{r.secret}</code>
                        <button type="button" className="vault-copy" onClick={() => navigator.clipboard?.writeText(r.secret)}>Copia</button>
                      </span>
                    )}
                  </span>
                  {r ? (
                    <button type="button" className="btn btn-ghost vault-btn" onClick={() => hide(e.id)}>Nascondi</button>
                  ) : (
                    <button type="button" className="btn btn-ghost vault-btn" onClick={() => reveal(e.id)}>
                      <Icon name="eye" size={14} /> Mostra
                    </button>
                  )}
                  <button type="button" className="vault-del" onClick={() => remove(e.id, e.label)} aria-label="Elimina">
                    <Icon name="x" size={15} />
                  </button>
                </div>
              );
            })
          ) : (
            <div className="rs-muted" style={{ padding: '24px 18px', fontSize: 13 }}>
              Nessuna credenziale salvata. Aggiungi la prima — verrà cifrata.
            </div>
          )}
        </div>
      </div>

      {newOpen && <NewSecretModal onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); void load(); }} />}
    </div>
  );
}

function SetupNote() {
  return (
    <div className="view">
      <div className="view-pad">
        <div className="rs-card" style={{ maxWidth: 540 }}>
          <div className="ops-head"><Icon name="lock" size={16} /> Cassaforte non attiva</div>
          <p className="rs-muted" style={{ fontSize: 13.5, lineHeight: 1.55, margin: 0 }}>
            La cassaforte è disattivata finché non imposti una chiave di cifratura <code>VAULT_KEY</code> sul server
            (32 byte — <code>openssl rand -hex 32</code>). Senza chiave nessun segreto viene salvato, così le password
            non finiscono mai in chiaro nel database.
          </p>
        </div>
      </div>
    </div>
  );
}

function NewSecretModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const clients = useAllClients();
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<VaultKind>('registrar');
  const [tenantSlug, setTenantSlug] = useState('');
  const [username, setUsername] = useState('');
  const [secret, setSecret] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!label.trim() || !secret || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.createSecret({
        label: label.trim(),
        kind,
        username: username.trim() || undefined,
        secret,
        note: note.trim() || undefined,
        tenantSlug: tenantSlug || undefined,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="mscrim open" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Nuovo segreto" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Nuovo segreto</h3>
          <button type="button" className="dr-close" onClick={onClose} aria-label="Chiudi"><Icon name="x" size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label>Etichetta</label>
            <input className="input" value={label} placeholder="Aruba — dominio carlotta" autoFocus onChange={(e) => setLabel(e.target.value)} />
          </div>
          <div className="field-row">
            <div className="field">
              <label>Tipo</label>
              <select className="input" value={kind} onChange={(e) => setKind(e.target.value as VaultKind)}>
                {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Cliente (opzionale)</label>
              <select className="input" value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)}>
                <option value="">— nessuno —</option>
                {clients.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="field">
            <label>Username / email (opzionale)</label>
            <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div className="field">
            <label>Password / chiave</label>
            <input className="input" type="password" value={secret} placeholder="••••••••" onChange={(e) => setSecret(e.target.value)} />
          </div>
          <div className="field">
            <label>Note (opzionale)</label>
            <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="rs-note">Il valore viene cifrato (AES-256-GCM) prima di essere salvato. In chiaro non viene mai scritto né nel database né nei log.</div>
          {error && <div className="rs-error">{error}</div>}
        </div>
        <div className="modal-foot">
          <div className="grow" />
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Annulla</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!label.trim() || !secret || busy}>
            {busy ? 'Salvo…' : 'Salva cifrato'}
          </button>
        </div>
      </div>
    </div>
  );
}
